import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPaceReport, updateBoard } from '../utils/api';
import toast from 'react-hot-toast';

/* Pace Board — fully automatic. Every sale is a record, so jobs sold and revenue come straight
   from the records (sold date this month, cancelled jobs left out, revenue = Change Order total
   or Contract Amount). Nothing to type in each day. The only setting is the monthly goal (admins). */

const money0 = (v) => isFinite(v) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : '$0';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Inter:wght@400;500;600;700;800&display=swap');
.pb-wrap{--bg1:#f7f2e7;--bg2:#efe7d6;--card:#fffdf8;--edge:rgba(201,146,42,.24);--ink:#173529;--muted:#7f8b81;--line:rgba(23,53,41,.09);--gold:#c9922a;--gold-d:#a9791d;--green:#1e6b4f;--red:#c0392b;--serif:'Cormorant Garamond',Georgia,serif;--sans:'Inter',-apple-system,system-ui,sans-serif;
  font-family:var(--sans);background:linear-gradient(180deg,var(--bg1),var(--bg2));color:var(--ink);min-height:calc(100vh - 40px);border-radius:20px;padding:24px 18px}
.pb-inner{max-width:1000px;margin:0 auto}
.pb-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;flex-wrap:wrap;gap:10px}
.pb-e{color:var(--gold);font-weight:700;letter-spacing:.2em;font-size:.62rem;margin-bottom:3px}
.pb-t{font-family:var(--serif);font-weight:700;font-size:2rem;line-height:1}
.pb-date{color:var(--muted);font-size:.85rem}
.pb-btn{border:0;border-radius:980px;padding:10px 18px;font-weight:700;cursor:pointer;font-family:inherit;font-size:.9rem}
.pb-btn.edit{background:linear-gradient(120deg,var(--gold),var(--gold-d));color:#14261c}
.pb-btn.save{background:var(--green);color:#fff}
.pb-btn.cancel{background:#efe7d4;color:var(--ink);margin-right:8px}
.pb-note{font-size:.78rem;color:var(--muted);margin:2px 0 16px}
.pb-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.pb-tile{background:var(--card);border:1px solid var(--edge);border-radius:20px;padding:20px 22px;box-shadow:0 14px 34px -18px rgba(20,40,25,.4)}
.pb-tile.pb-editing{border-color:var(--gold)}
.pb-lbl{color:var(--gold);font-weight:800;letter-spacing:.12em;font-size:.72rem;text-transform:uppercase}
.pb-big{font-family:'Fraunces',Georgia,serif;font-weight:800;font-size:2.9rem;letter-spacing:-.02em;line-height:1.05;margin-top:8px;font-variant-numeric:tabular-nums}
.pb-big.money{color:var(--green)}
.pb-sub{color:var(--muted);font-size:.9rem;margin-top:8px;font-weight:500}
.pb-sub b{color:var(--ink);font-weight:700}
.pb-pill{display:inline-block;font-weight:700;font-size:.72rem;padding:3px 10px;border-radius:980px;margin-left:4px}
.pb-pill.up{background:rgba(30,107,79,.12);color:var(--green)}
.pb-pill.down{background:#fdece9;color:var(--red)}
.pb-bar{height:12px;border-radius:8px;background:#eef1ee;overflow:hidden;margin-top:12px}
.pb-bar i{display:block;height:100%;border-radius:8px;transition:width .5s}
.pb-prev{display:flex;justify-content:space-between;border-top:1px solid var(--line);margin-top:14px;padding-top:12px;font-size:.92rem}
.pb-prev .k{color:var(--muted);font-weight:500}.pb-prev .v{font-weight:800;font-family:'Fraunces',Georgia,serif;font-variant-numeric:tabular-nums}
.pb-ed{display:flex;align-items:center;gap:6px;margin-top:12px;flex-wrap:wrap}
.pb-ed label{font-size:.74rem;color:var(--muted);font-weight:600}
.pb-ed input{border:1.5px solid var(--edge);border-radius:8px;padding:6px 9px;font:inherit;font-weight:700;width:120px;color:var(--ink);outline:0;background:#fff}
@media(max-width:760px){.pb-grid{grid-template-columns:1fr}.pb-big{font-size:2.4rem}.pb-t{font-size:1.6rem}}
.pb-tabs{display:flex;gap:8px;margin:2px 0 18px}
.pb-tab{font-family:var(--sans);font-size:.9rem;font-weight:700;padding:8px 20px;border-radius:999px;border:1px solid var(--edge);background:var(--card);color:var(--muted);cursor:pointer;transition:all .15s}
.pb-tab:hover{color:var(--ink)}
.pb-tab.on{background:var(--green);color:#fff;border-color:var(--green)}
.pb-hist{background:var(--card);border:1px solid var(--edge);border-radius:20px;padding:6px;box-shadow:0 14px 34px -18px rgba(20,40,25,.4);overflow-x:auto}
.pb-htable{width:100%;border-collapse:collapse;font-family:var(--sans);font-size:.9rem}
.pb-htable th{text-align:left;color:var(--muted);font-weight:700;font-size:.7rem;letter-spacing:.04em;text-transform:uppercase;padding:13px 14px;border-bottom:2px solid var(--line);white-space:nowrap}
.pb-htable td{padding:12px 14px;border-bottom:1px solid var(--line);color:var(--ink);white-space:nowrap;font-variant-numeric:tabular-nums}
.pb-htable tr:last-child td{border-bottom:0}
.pb-htable td.d{font-weight:700;color:var(--green)}
.pb-htable td.rd{color:var(--red);font-weight:700}
.pb-htable tbody tr:hover td{background:rgba(201,146,42,.05)}
.pb-miss{background:#fff8e6;border:1px solid #f0d99a;border-radius:12px;padding:13px 16px;margin-bottom:16px;font-family:var(--sans);font-size:.9rem;color:#6b4e10}
.pb-miss b{font-weight:800}
.pb-chip{display:inline-block;margin:5px 6px 0 0;padding:5px 12px;border-radius:999px;border:1px solid #e0b24a;background:#fff;color:#8a6414;font-weight:700;font-size:.82rem;cursor:pointer;font-family:var(--sans)}
.pb-chip:hover{background:#e0b24a;color:#fff}
.pb-addbtn{font-family:var(--sans);font-size:.85rem;font-weight:700;padding:7px 15px;border-radius:999px;border:1px solid var(--green);background:var(--card);color:var(--green);cursor:pointer}
.pb-addbtn:hover{background:var(--green);color:#fff}
.pb-rowbtn{font-family:var(--sans);font-size:.78rem;font-weight:700;padding:4px 11px;border-radius:7px;border:1px solid var(--edge);background:var(--card);color:var(--muted);cursor:pointer}
.pb-rowbtn:hover{border-color:var(--green);color:var(--green)}
.pb-modal{position:fixed;inset:0;background:rgba(12,28,22,.45);display:flex;align-items:center;justify-content:center;padding:18px;z-index:900}
.pb-sheet{background:var(--card);border-radius:16px;max-width:560px;width:100%;max-height:92vh;overflow:auto;padding:22px 24px;font-family:var(--sans);box-shadow:0 18px 50px rgba(0,0,0,.28)}
.pb-sheet h3{font-family:'Fraunces',Georgia,serif;font-size:1.3rem;margin:0 0 4px;color:var(--ink)}
.pb-sheet .sub{color:var(--muted);font-size:.86rem;margin-bottom:16px}
.pb-fg{display:grid;grid-template-columns:1fr 1fr;gap:11px 14px}
.pb-fg label{display:block;font-size:.73rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px}
.pb-fg input{width:100%;padding:8px 10px;border:1px solid var(--edge);border-radius:8px;font-family:var(--sans);font-size:.92rem;font-variant-numeric:tabular-nums;box-sizing:border-box}
.pb-fg input:focus{outline:none;border-color:var(--green)}
.pb-acts{display:flex;gap:9px;margin-top:20px;flex-wrap:wrap}
.pb-acts .sp{flex:1}
@media(max-width:560px){.pb-fg{grid-template-columns:1fr}}
.pb-histnote{background:var(--card);border:1px solid var(--edge);border-radius:20px;padding:28px;text-align:center;color:var(--muted);font-family:var(--sans)}

.pb-goaled{display:flex;gap:8px;align-items:center;margin-top:12px;flex-wrap:wrap}
.pb-goaled input{border:1.5px solid var(--gold);border-radius:8px;padding:7px 10px;font:inherit;font-weight:700;width:150px;color:var(--ink);outline:0;background:#fff}
.pb-pen{border:0;background:none;color:var(--gold-d);font-weight:700;font-size:.78rem;cursor:pointer;margin-left:8px;text-decoration:underline}
.pb-auto{display:inline-flex;align-items:center;gap:6px;font-size:.74rem;font-weight:700;color:var(--green);background:rgba(30,107,79,.09);border-radius:999px;padding:4px 11px}
.pb-auto i{width:7px;height:7px;border-radius:50%;background:#34c759;box-shadow:0 0 0 3px rgba(52,199,89,.2)}
`;

export default function PerformanceBoard() {
  const { isAdmin } = useAuth();
  const [d, setD] = useState(null);
  const [goalEdit, setGoalEdit] = useState(null);   // string while editing the monthly goal
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const id = 'cpr-serif-fonts';
    if (!document.getElementById(id)) {
      const l = document.createElement('link');
      l.id = id; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Inter:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  const load = useCallback(() => getPaceReport().then(setD).catch(() => toast.error('Could not load the board')), []);
  useEffect(() => { load(); const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);

  async function saveGoal() {
    const v = parseFloat(String(goalEdit).replace(/[^0-9.]/g, ''));
    if (!isFinite(v) || v <= 0) return toast.error('Enter the monthly goal in dollars');
    setSaving(true);
    try { await updateBoard({ monthly_goal: v }); setGoalEdit(null); toast.success('Monthly goal saved'); load(); }
    catch { toast.error('Could not save the goal'); }
    finally { setSaving(false); }
  }

  if (!d) return <div style={{ padding: 40, textAlign: 'center', color: '#7f8b81' }}>Loading board…</div>;

  const ahead = d.diff >= 0;
  const pctOfGoal = d.goal > 0 ? Math.min(100, Math.round(d.month.revenue / d.goal * 100)) : 0;
  const dateStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const monthName = new Date(d.monthStart + 'T12:00:00Z').toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long' });
  const vsLast = d.month.revenue - d.lastMonthToDate.revenue;

  return (
    <div className="pb-wrap">
      <style>{CSS}</style>
      <div className="pb-inner">
        <div className="pb-top">
          <div>
            <div className="pb-e">CPR HOME SOLUTIONS</div>
            <div className="pb-t">Pace Board</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="pb-date">{dateStr}</div>
            <span className="pb-auto"><i />Live from the records</span>
          </div>
        </div>
        <div className="pb-note">Every job sold this month, straight from the records. Cancelled jobs are not counted. Updates on its own.</div>

        <div className="pb-grid">
          {/* SOLD THIS MONTH */}
          <div className="pb-tile">
            <div className="pb-lbl">Sold in {monthName}</div>
            <div className="pb-big money">{money0(d.month.revenue)}</div>
            <div className="pb-sub"><b>{d.month.jobs}</b> job{d.month.jobs === 1 ? '' : 's'} sold · avg <b>{money0(d.avgJob)}</b> per job</div>
            <div className="pb-prev"><span className="k">Sold today</span><span className="v">{money0(d.today_sold.revenue)}{d.today_sold.jobs ? ` · ${d.today_sold.jobs} job${d.today_sold.jobs === 1 ? '' : 's'}` : ''}</span></div>
            <div className="pb-prev"><span className="k">Same point last month</span><span className="v">{money0(d.lastMonthToDate.revenue)}
              {d.lastMonthToDate.revenue > 0 && <span className={`pb-pill ${vsLast >= 0 ? 'up' : 'down'}`}>{vsLast >= 0 ? '▲ ' : '▼ '}{money0(Math.abs(vsLast))}</span>}</span></div>
          </div>

          {/* MONTHLY GOAL + PACING */}
          <div className="pb-tile">
            <div className="pb-lbl">Monthly Goal{isAdmin && goalEdit === null && <button className="pb-pen" onClick={() => setGoalEdit(String(d.goal || ''))}>change</button>}</div>
            <div className="pb-big money">{money0(d.goal)}</div>
            <div className="pb-sub">Pacing <b>{money0(d.pacing)}</b>
              {d.goal > 0 && <span className={`pb-pill ${ahead ? 'up' : 'down'}`}>{ahead ? '▲ ahead ' : '▼ behind '}{money0(Math.abs(d.diff))}</span>}
            </div>
            <div className="pb-bar"><i style={{ width: pctOfGoal + '%', background: 'linear-gradient(90deg,#2d6b57,#1e6b4f)' }} /></div>
            <div className="pb-sub" style={{ fontSize: '.8rem' }}>{pctOfGoal}% of the goal sold · avg {money0(d.avgPerDay)}/day × {d.workdays.total} working days</div>
            {goalEdit !== null && (
              <div className="pb-goaled">
                <input autoFocus value={goalEdit} onChange={e => setGoalEdit(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveGoal()} placeholder="e.g. 600000" />
                <button className="pb-btn save" onClick={saveGoal} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button className="pb-btn cancel" onClick={() => setGoalEdit(null)}>Cancel</button>
              </div>
            )}
          </div>

          {/* DAILY GOAL */}
          <div className="pb-tile">
            <div className="pb-lbl">Daily Goal</div>
            <div className="pb-big money">{money0(d.dailyGoal)}</div>
            <div className="pb-sub">to sell each working day left to hit {money0(d.goal)}</div>
            <div className="pb-prev"><span className="k">Yesterday sold</span><span className="v">{money0(d.yesterday.revenue)}{d.yesterday.jobs ? ` · ${d.yesterday.jobs} job${d.yesterday.jobs === 1 ? '' : 's'}` : ''}</span></div>
            <div className="pb-prev"><span className="k">Still to sell this month</span><span className="v">{money0(Math.max(0, d.goal - d.month.revenue))}</span></div>
          </div>

          {/* DAYS LEFT */}
          <div className="pb-tile">
            <div className="pb-lbl">Days Left</div>
            <div className="pb-big">{d.workdays.left}</div>
            <div className="pb-sub">working days left this month, today included · Sundays excluded</div>
            <div className="pb-prev"><span className="k">Working days this month</span><span className="v">{d.workdays.total}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
